import i18n from "i18next";
import { ShopOrderApi } from "src/api/generated/erp/production/api/shopOrder/shopOrderApi.ts";
import { ShopOrderActionApi } from "src/api/generated/erp/production/api/shopOrder/shopOrderActionApi.ts";
import type { ShowConfirmDialogFunc } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { ShopOrderBomApi } from "src/api/generated/erp/production/api/shopOrderBom/shopOrderBomApi.ts";

export async function cancelShopOrders({
	shopOrderIds,
	showConfirmDialog,
	onlyShopOrdersCreatedByMe = false,
}: {
	shopOrderIds: number[];
	showConfirmDialog: ShowConfirmDialogFunc;
	/** Cancels through the endpoint that accepts only shop orders the user created. */
	onlyShopOrdersCreatedByMe?: boolean;
}) {
	if (shopOrderIds.length === 0) return;

	const confirmed = await showConfirmDialog({
		title: i18n.t("cancel"),
		message: i18n.t("confirm_cancel_shop_order"),
	});
	if (!confirmed) return;

	const shopOrderIdsToCancelled: number[] = [];

	for (const shopOrderId of shopOrderIds) {
		const canCancel = await checkShopOrderCanBeCancelled({
			shopOrderId,
			showConfirmDialog,
		});
		if (canCancel) {
			shopOrderIdsToCancelled.push(shopOrderId);
		}
	}

	if (shopOrderIdsToCancelled.length === 0) return;

	if (onlyShopOrdersCreatedByMe) {
		await ShopOrderActionApi.cancelShopOrdersCreatedByMe({ shopOrderIds: shopOrderIdsToCancelled });
	} else {
		await ShopOrderActionApi.cancelShopOrders({ shopOrderIds: shopOrderIdsToCancelled });
	}
}

async function checkShopOrderCanBeCancelled({
	shopOrderId,
	showConfirmDialog,
}: {
	shopOrderId: number;
	showConfirmDialog: ShowConfirmDialogFunc;
}): Promise<boolean> {
	const linesWithReceivedPurchaseOrders = await ShopOrderApi.getShopOrderLinesWithReceivedPurchaseOrderLines({
		shopOrderId: shopOrderId,
	});

	if (linesWithReceivedPurchaseOrders.length > 0) {
		const confirmed = await showConfirmDialog({
			title: i18n.t("are_you_sure"),
			message: i18n.t("shop_order_has_lines_with_received_purchase_orders"),
		});
		if (!confirmed) return false;
	}

	const shopOrderLines = await ShopOrderBomApi.getShopOrderBom({ shopOrderId: shopOrderId });
	const shopOrderWarehouseAcquisitionLines = shopOrderLines.filter((line) => line.acquisitionMethod === "WAREHOUSE");
	if (shopOrderWarehouseAcquisitionLines.some((line) => line.reservedQuantity > line.pickedQuantity)) {
		await showConfirmDialog({
			title: i18n.t("can_not_cancel"),
			message: i18n.t("shop_order_warehouse_lines_has_reservations.revert_reservations_first"),
		});
		return false;
	}
	if (
		shopOrderWarehouseAcquisitionLines.some(
			(line) => line.pickedQuantity > 0 && line.pickedQuantity === line.reservedQuantity,
		)
	) {
		const confirmed = await showConfirmDialog({
			title: i18n.t("are_you_sure"),
			message: i18n.t("shop_order_has_picked_lines"),
		});
		if (!confirmed) return false;
	}

	return true;
}
